import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue } from 'jotai';

import { Button, ChipCheckboxGroup, RadioGroup, TextArea } from '@restworld/ui-ds-base';
import { EmployerReportDetailGroup, EmployerReportReason } from '@restworld/utility-types';

import { ReportDraft, reportDraftAtom, selectedJobApplicationAtom } from '../../../../employer-job-listing.atoms';
import { useReportReasons } from '../../../../hooks/use-job-application-report';
import { BodyWrapper, FooterRow, Header, Subtitle, Title } from './report-candidate-layout';
import { getJobApplicationId } from '../../../../candidate-visibility';

/**
 * Toggles one detail inside its group. A `single` group holds at most one detail: picking another one
 * replaces it, picking the same one again clears it. A `multiple` group adds or removes the detail.
 */
export function toggleReportDetail(
  detailKeys: string[],
  group: EmployerReportDetailGroup,
  detailKey: string
): string[] {
  if (detailKeys.includes(detailKey)) return detailKeys.filter((key) => key !== detailKey);
  if (group.selection === 'multiple') return [...detailKeys, detailKey];

  const groupKeys = group.details.map((detail) => detail.key);
  return [...detailKeys.filter((key) => !groupKeys.includes(key)), detailKey];
}

export const canContinueReport = (draft: ReportDraft, reason: EmployerReportReason | undefined) =>
  !!reason && (!reason.message_required || draft.message.trim().length > 0);

export function useSelectedReportReason() {
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const draft = useAtomValue(reportDraftAtom);
  const { data: reasons = [], isLoading } = useReportReasons(getJobApplicationId(selectedJobApplication), true);
  const reason = useMemo(() => reasons.find(({ key }) => key === draft.reasonKey), [reasons, draft.reasonKey]);

  return { reasons, reason, draft, isLoading };
}

export function ReportReasonsStep() {
  const { t } = useTranslation('job-positions-page');
  const selectedJobApplication = useAtomValue(selectedJobApplicationAtom);
  const [draft, setDraft] = useAtom(reportDraftAtom);
  const { reasons, reason, isLoading } = useSelectedReportReason();

  const reasonOptions = useMemo(
    () => reasons.map(({ key, label, description }) => ({ id: key, key, label, description })),
    [reasons]
  );

  // A different reason has different details: changing reason clears them.
  const onReasonChange = useCallback(
    (reasonKey: string) =>
      setDraft((current) => (current.reasonKey === reasonKey ? current : { ...current, reasonKey, detailKeys: [] })),
    [setDraft]
  );

  return (
    <BodyWrapper>
      <Header>
        <Title>
          {t('report.title', {
            name: selectedJobApplication.worker?.user?.name ?? selectedJobApplication.worker?.name ?? '',
          })}
        </Title>
        <Subtitle>{t('report.subtitle')}</Subtitle>
      </Header>

      <div className="flex flex-col gap-6 w-full">
        <RadioGroup
          id="report-reason"
          orientation="vertical"
          options={reasonOptions}
          returnKey="key"
          value={draft.reasonKey ?? ''}
          onChange={onReasonChange}
          isLoading={isLoading}
          isFullWidth
        />

        {reason?.detail_groups.map((group) => {
          const groupKeys = group.details.map((detail) => detail.key);
          return (
            <div key={group.key} className="flex flex-col gap-2">
              {group.label ? <p className="font-medium text-neutral-700 text-base">{group.label}</p> : null}
              <ChipCheckboxGroup
                testId={`report-detail-group-${group.key}`}
                options={group.details.map(({ key, label }) => ({ id: key, key, label }))}
                value={draft.detailKeys.filter((key) => groupKeys.includes(key))}
                onChangeHandler={(detailKey) =>
                  setDraft((current) => ({
                    ...current,
                    detailKeys: toggleReportDetail(current.detailKeys, group, detailKey),
                  }))
                }
              />
            </div>
          );
        })}

        <TextArea
          id="report-message"
          testId="report-message"
          label={reason?.message_required ? t('report.message_label') : t('report.message_label_optional')}
          supportText={reason?.message_required ? t('report.message_required_note') : undefined}
          value={draft.message}
          // Same cap as the backend changeset (WorkerEmployerReport, 2000 characters).
          maxLength={2000}
          showMaxLength={false}
          onChange={(message) => setDraft((current) => ({ ...current, message }))}
        />
      </div>
    </BodyWrapper>
  );
}

export function ReportReasonsFooter({ onCancel, onContinue }: { onCancel: () => void; onContinue: () => void }) {
  const { t } = useTranslation('job-positions-page');
  const { reason, draft } = useSelectedReportReason();

  return (
    <FooterRow>
      <Button type="button" variant="tertiary" onClick={onCancel}>
        {t('report.cancel')}
      </Button>
      <Button type="button" disabled={!canContinueReport(draft, reason)} onClick={onContinue}>
        {t('report.continue')}
      </Button>
    </FooterRow>
  );
}
